import { useState } from "react";
import { DownloadIcon, Edit3Icon, FileDownIcon, FilterIcon, List, PlusIcon, RotateCcw, Trash2, TrashIcon, UploadIcon } from "lucide-react";
import { Button, LinkButton } from "./Buttons";
import Table2 from "./Table";
import { user_profile_image_url } from "./ImageUrls";
import StatusPill from "./StatusPill";
import { ToastMessage } from "./Utils";
import { useToggleStatusMutation } from "../stores/api/toggleStatus.api";
import { useExcelExportMutation, useExcelImportMutation } from "../stores/api/excel.api";
import { handleError } from "../stores/components/handleError";
import ExcelImportModal from "./ExcelImportModal";
import ExcelExportModal from "./ExcelExportModal";
import Img from "../Pages/User/components/Img";
import { useNavigate } from "react-router-dom";

const formatCurrency = (value) => {
  const number = Number(value);
  if (Number.isNaN(number)) return value ?? "-";
  return number.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const getValue = (row, key) => key.split(".").reduce((acc, k) => acc?.[k], row);

/**
 * TableListing
 *
 * inlineEdit:   (row, onSave, onCancel) => ReactNode   — form rendered below the row being edited
 * inlineCreate: (onSave, onCancel) => ReactNode        — form rendered in a panel above the table
 *
 * When inlineEdit is provided, an Edit button is automatically added to each row's actions.
 * When inlineCreate is provided, a Create button appears in the toolbar.
 * Both can coexist with the existing `actions` prop (custom actions render alongside the auto-edit button).
 */
const TableListing = ({
  headers = [],
  loading = false,
  hasFilter = false,
  filterButtonAction = null,
  onFilter = null,
  createButton = false,
  createLink = "",
  actions = null,
  paramCreateData = null,
  paramName = "",
  excelTemplateExport = null,
  handleBulk = null,
  paginationData = {},
  onPageChange = null,
  onSearch = null,
  onRowsPerPageChange = null,
  trashed = false,
  setTrashed = null,
  search = "",
  setSearch = null,
  onTrashedChange = null,
  inlineEdit = null,
  inlineCreate = null,
  onInlineEditSave = null,
  onInlineCreateSave = null,
  onSortChange = null,
  statusModule = null,
  excelModule = null,
  buttonLoading = false,
  isExcelExport = false,
  exportData = null,
  isExcelImport = false
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const runBulk = (rows, action) => {
    handleBulk?.(rows, action);
    setSelectionResetKey((k) => k + 1);
  };
  const [toggleStatus] = useToggleStatusMutation();
  const [excelImport, { isLoading: importing }] = useExcelImportMutation();
  const [excelExport, { isLoading: exporting }] = useExcelExportMutation();

  const moduleForExcel = excelModule || statusModule;

  const handleImportSubmit = async ({ body }) => {
    if (!moduleForExcel) {
      ToastMessage({ type: "info", title: "Coming soon", message: "Import not wired for this module." });
      return;
    }
    try {
      const res = await excelImport({ module: moduleForExcel, body }).unwrap();
      ToastMessage({ type: "success", title: "Imported", message: res?.message || "Import received." });
      setImportOpen(false);
    } catch (err) {
      handleError(err, "Failed to import.");
    }
  };

  const handleExportSubmit = async ({ body }) => {
    if (!moduleForExcel) {
      ToastMessage({ type: "info", title: "Coming soon", message: "Export not wired for this module." });
      return;
    }
    try {
      const res = await excelExport({ module: moduleForExcel, body }).unwrap();
      ToastMessage({ type: "success", title: "Exported", message: res?.message || "Export request sent." });
      setExportOpen(false);
    } catch (err) {
      handleError(err, "Failed to export.");
    }
  };

  const handleStatusToggle = async (row) => {
    if (!statusModule) {
      ToastMessage({ type: "info", title: "Coming soon", message: "Status toggle not wired for this module." });
      return;
    }
    try {
      await toggleStatus({ module: statusModule, id: row.id }).unwrap();
      ToastMessage({ type: "success", title: "Status updated", message: "Status changed successfully." });
    } catch (err) {
      handleError(err, "Failed to toggle status.");
    }
  };

  const handleTrashedChange = (value) => {
    setTrashed?.(value);
    onTrashedChange?.(value);
  };
  const handleTemplateExport = async () => {
    if (excelTemplateExport) {
      return await excelTemplateExport();
    }
  }
  const handleExportData = async () => {
    if (exportData) {
      return await exportData();
    }
  }

  const navigate = useNavigate();
  const startEdit = (row) => { setEditingId(row.id); setIsCreating(false); };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = (data) => { onInlineEditSave?.(data); cancelEdit(); };

  const startCreate = () => { setIsCreating(true); cancelEdit(); };
  const cancelCreate = () => setIsCreating(false);
  const saveCreate = (data) => { onInlineCreateSave?.(data); cancelCreate(); };

  const hasInlineActions = inlineEdit || actions;
  const tableHeaders = hasInlineActions
    ? [...headers, { key: "actions", label: "Actions", sortable: false }]
    : headers;

  const serial = (index) =>
    (paginationData.current_page - 1) * paginationData.limit + index + 1;

  return (
    <div className="bg-white dark:bg-slate-800 shadow-sm rounded-md overflow-hidden">
      {isCreating && inlineCreate && (
        <div className="border-b border-green-200 dark:border-green-500/30 bg-green-50/60 dark:bg-green-500/5 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="w-0.5 self-stretch rounded-full bg-green-400 dark:bg-green-500 flex-shrink-0" />
            <div className="flex-1 space-y-1">
              <p className="text-xs font-semibold text-green-600 dark:text-white-400 uppercase tracking-wide">New Record</p>
              {inlineCreate(saveCreate, cancelCreate)}
            </div>
          </div>
        </div>
      )}

      <Table2
        headers={tableHeaders}
        onSortChange={onSortChange}
        data={paginationData?.data ?? []}
        loading={loading}
        resetSelectionKey={selectionResetKey}
        multiSelect={
          handleBulk
            ? { enabled: true, bulkActionShowThreshold: 1, onSelectionChange: setSelectedRows }
            : undefined
        }
        search={
          onSearch || setSearch
            ? {
              placeholder: "Search...",
              by: "__search__",
              value: search,
              onChange: (val) => { setSearch?.(val); onSearch?.(val); },
            }
            : undefined
        }
        pagination={
          paginationData?.total_pages
            ? { data: paginationData, onPageChange, onRowsPerPageChange }
            : undefined
        }
      >
        {({ row, index, RowComponent, CellComponent, CheckboxCell, isSelected }) => (
          <RowComponent
            key={row.__rowKey ?? row.id ?? index}
            selected={isSelected}
            isEditing={inlineEdit ? editingId === row.id : undefined}
            FormComponent={
              inlineEdit && editingId === row.id
                ? inlineEdit(row, saveEdit, cancelEdit)
                : undefined
            }
          >
            {CheckboxCell}
            {headers.map((header, idx) => {
              const isCheckbox = header.key === "checkboxes" || header.type === "checkbox" || header.checkbox;
              if (isCheckbox) return null;

              const isBadge = header.type === "badge" || header.isBadge;
              const isStatus = header.type === "status";
              const isProgressBar = header.type === "progressbar" || header.progress;
              const isAmount = header.type === "amount" || header.amount;
              const isImage = header.type === "image";
              const rawValue = header.key ? getValue(row, header.key) : null;

              const displayValue = header.render
                ? header.render(row)
                : header.format
                  ? header.format(rawValue, row)
                  : rawValue;
              const badgeValue = displayValue ?? "-";
              const badgeClass = isBadge
                ? (header.badgeColors?.[badgeValue] ?? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100")
                : "";
              const progressValue = Number(row[header.key] ?? 0);

              const statusActive = Boolean(rawValue);
              const statusWhen = header.when ?? { active: "Active", inActive: "Inactive" };
              const statusLabel = statusActive ? statusWhen.active : statusWhen.inActive;

              return (
                <CellComponent key={idx}>
                  {isImage ? (
                    <Img
                      src={(header.imageBaseUrl ?? user_profile_image_url) + rawValue}
                      alt={header.label}
                      className="h-10 w-10 shadow object-cover"
                    />
                  ) : isStatus ? (
                    <StatusPill
                      value={statusLabel}
                      variant={statusActive ? "success" : "neutral"}
                      flicker={statusActive ? {} : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusToggle(row);
                      }}
                    />
                  ) : isBadge ? (
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${badgeClass}`}>
                      {badgeValue}
                    </span>
                  ) : isProgressBar ? (
                    <div className="relative h-3.5 w-[150px] rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${progressValue >= 80 ? "bg-emerald-500" : progressValue >= 50 ? "bg-amber-500" : "bg-green-500"}`}
                        style={{ width: `${progressValue}%` }}
                      />
                      <span className={`absolute inset-x-0 inset-y-0 flex items-center justify-center text-[10px] font-semibold pointer-events-none ${progressValue < 50 ? "text-slate-900 dark:text-white" : "text-white"}`}>
                        {progressValue}%
                      </span>
                    </div>
                  ) : isAmount ? (
                    formatCurrency(rawValue)
                  ) : header.key === "id" ? (
                    serial(index)
                  ) : (
                    displayValue
                  )}
                </CellComponent>
              );
            })}

            {hasInlineActions && (
              <CellComponent>
                <div className="flex gap-2">
                  {inlineEdit && (
                    <Button
                      onClick={() => editingId === row.id ? cancelEdit() : startEdit(row)}
                      title={editingId === row.id ? "Cancel" : "Edit"}
                      icon={Edit3Icon}
                      className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
                    />
                  )}
                  {actions?.(row)}
                </div>
              </CellComponent>
            )}
          </RowComponent>
        )}

        {/* Bulk actions node (2nd child) */}
        <div className="flex justify-end gap-3 items-center py-1">
          {isExcelExport && <Button
            icon={UploadIcon}
            onClick={handleExportData}
            title="Export"
            disabled={buttonLoading}
            className="bg-blue-100 flex gap-2 items-center text-blue-600 px-3 py-1 rounded-[6px] hover:bg-blue-600 hover:text-white transition shadow-sm dark:bg-blue-200/10 dark:text-blue-300 dark:hover:bg-blue-500 dark:hover:text-white"
          />}
          {excelTemplateExport && <Button
            icon={FileDownIcon}
            onClick={handleTemplateExport}
            title="Export Template"
            disabled={buttonLoading}
            className="bg-yellow-100 flex gap-2 items-center text-green-600 px-3 py-1 rounded-[6px] hover:bg-yellow-600 hover:text-white transition shadow-sm dark:bg-blue-200/10 dark:text-blue-300 dark:hover:bg-blue-500 dark:hover:text-white"
          />}
          {isExcelImport && <Button
            icon={DownloadIcon}
            onClick={() => setImportOpen(true)}
            title="Import"
            disabled={importing}
            className="bg-purple-100 flex gap-2 items-center text-purple-600 px-3 py-1 rounded-[6px] hover:bg-purple-600 hover:text-white transition shadow-sm dark:bg-purple-200/10 dark:text-purple-300 dark:hover:bg-purple-500 dark:hover:text-white"
          />}
          {inlineCreate && (
            <Button
              onClick={isCreating ? cancelCreate : startCreate}
              icon={PlusIcon}
              title={isCreating ? "Cancel" : "Create"}
              className="bg-green-100 flex gap-2 items-center text-green-500 px-3 py-1 rounded-[6px] hover:bg-green-600 hover:text-white transition dark:bg-green-200/10 dark:text-white-300 dark:hover:bg-green-500 dark:hover:text-white"
            />
          )}
          {hasFilter && filterButtonAction && (

            <Button
              icon={FilterIcon}
              title="Filters"
              onClick={filterButtonAction}
              className="bg-orange-100 flex gap-3 items-center text-orange-500 px-3 py-1 rounded-[6px] hover:bg-orange-600 hover:text-white transition shadow-sm dark:bg-orange-200/10 dark:text-white-300 dark:hover:bg-orange-500 dark:hover:text-white"
            />

          )}
          {createButton && createLink && (
            paramCreateData && paramName ? (
              <Button
                icon={PlusIcon}
                title="Add New"
                onClick={() =>
                  navigate(createLink, {
                    state: { [paramName]: paramCreateData },
                  })
                }
                className="bg-green-100 flex gap-3 items-center text-green-500 px-3 py-1 rounded-[6px] hover:bg-green-600 hover:text-white transition shadow-sm dark:bg-green-200/10 dark:text-white-300 dark:hover:bg-green-500 dark:hover:text-white"
              />
            ) : (
              <LinkButton
                icon={PlusIcon}
                to={createLink}
                title="Add New"
                className="bg-green-100 flex gap-3 items-center text-green-500 px-3 py-1 rounded-[6px] hover:bg-green-600 hover:text-white transition shadow-sm dark:bg-green-200/10 dark:text-white-300 dark:hover:bg-green-500 dark:hover:text-white"
              />
            )
          )}
          {handleBulk && (
            <Button
              icon={TrashIcon}
              disabled={selectedRows.length === 0}
              onClick={() => runBulk(selectedRows, trashed ? "destroy" : "delete")}
              title="Delete Selected"
              className="bg-red-100 flex gap-3 items-center cursor-pointer text-red-500 px-3 py-1 rounded-[6px] hover:bg-red-600 hover:text-white transition shadow-sm dark:bg-red-200/10 dark:text-red-300 dark:hover:bg-red-500 dark:hover:text-white"
            />
          )}
          {trashed !== null && trashed && (
            <Button
              icon={RotateCcw}
              disabled={selectedRows.length === 0}
              onClick={() => runBulk(selectedRows, "restore")}
              title="Restore Selected"
              className="bg-green-100 flex gap-3 cursor-pointer items-center text-green-500 px-3 py-1 rounded-[6px] hover:bg-green-600 hover:text-white transition shadow-sm dark:bg-green-200/10 dark:text-white-300 dark:hover:bg-green-500 dark:hover:text-white"
            />
          )}
          <Button
            icon={trashed ? List : Trash2}
            onClick={() => handleTrashedChange(!trashed)}
            title={trashed ? "View List" : "View Trashed"}
            className="bg-gray-300 flex gap-3 items-center text-gray-500 px-3 py-1 rounded-[6px] hover:bg-gray-600 cursor-pointer hover:text-white transition shadow-sm dark:bg-gray-200/10 dark:text-gray-300 dark:hover:bg-gray-500 dark:hover:text-white"
          />
        </div>
      </Table2>

      <ExcelImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSubmit={handleImportSubmit}
        loading={importing}
      />
      <ExcelExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onSubmit={handleExportSubmit}
        loading={exporting}
      />
    </div>
  );
};

export default TableListing;
